"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import Container from "@/components/ui/Container";
import { gsap, useGSAP } from "@/lib/gsap";
import { useNearViewport } from "@/hooks/useNearViewport";
import TextReveal from "@/components/ui/TextReveal";
import AnimatedEyebrow from "@/components/ui/AnimatedEyebrow";
import { Send, Zap, ChevronRight, ChevronDown } from "lucide-react";

/* ── Chains ─────────────────────────────────────────────────────────────────── */
const CHAINS = [
  { id: "eth",  name: "Ethereum",  tag: "L1 · EVM",      color: "#6C63FF", baseLatency: 11, baseP99: 28, blockTime: 12,    startBlock: 19_847_322  },
  { id: "arb",  name: "Arbitrum",  tag: "L2 · Rollup",   color: "#12AAFF", baseLatency: 7,  baseP99: 19, blockTime: 0.25,  startBlock: 204_710_888 },
  { id: "sol",  name: "Solana",    tag: "L1 · non-EVM",  color: "#9945FF", baseLatency: 22, baseP99: 51, blockTime: 0.4,   startBlock: 299_182_040 },
  { id: "base", name: "Base",      tag: "L2 · OP Stack", color: "#0052FF", baseLatency: 9,  baseP99: 22, blockTime: 2,     startBlock: 14_512_003  },
  { id: "op",   name: "Optimism",  tag: "L2 · OP Stack", color: "#FF0420", baseLatency: 8,  baseP99: 20, blockTime: 2,     startBlock: 122_498_410 },
] as const;

type Chain = (typeof CHAINS)[number];

/* ── Methods ────────────────────────────────────────────────────────────────── */
const EVM_METHODS = [
  { id: "eth_blockNumber",      label: "eth_blockNumber",      params: "[]",                                                                              result: '"0x12A3F91"'                                                                                  },
  { id: "eth_getBalance",       label: "eth_getBalance",       params: '["0xd8dA…6045", "latest"]',                                                       result: '"0x1BC16D674EC80000"'                                                                          },
  { id: "eth_gasPrice",         label: "eth_gasPrice",         params: "[]",                                                                              result: '"0x59682F00"'                                                                                  },
  { id: "eth_getBlockByNumber", label: "eth_getBlockByNumber", params: '["latest", false]',                                                               result: '{ "number": "0x12A3F91", "hash": "0xab3c…f9e1", "gasUsed": "0xE4E1C0" }'                       },
  { id: "eth_call",             label: "eth_call",             params: '[{ "to": "0xA0b8…eB48", "data": "0x18160ddd" }, "latest"]',                       result: '"0x000000…000000003B9ACA00"'                                                                  },
] as const;

const SOL_METHODS = [
  { id: "getSlot",              label: "getSlot",              params: "[]",                                                                              result: "299182041"                                                                                     },
  { id: "getBalance",           label: "getBalance",           params: '["9xQ…tK8a"]',                                                                    result: '{ "context": { "slot": 299182041 }, "value": 2039280 }'                                        },
  { id: "getLatestBlockhash",   label: "getLatestBlockhash",   params: '[{ "commitment": "finalized" }]',                                                 result: '{ "blockhash": "EkSn…9tPq", "lastValidBlockHeight": 299182441 }'                                },
  { id: "getBlockHeight",       label: "getBlockHeight",       params: "[]",                                                                              result: "278934512"                                                                                     },
  { id: "getAccountInfo",       label: "getAccountInfo",       params: '["9xQ…tK8a", { "encoding": "base64" }]',                                          result: '{ "lamports": 2039280, "owner": "Token…ProgM", "executable": false }'                          },
] as const;

type Method = (typeof EVM_METHODS)[number] | (typeof SOL_METHODS)[number];

function methodsFor(chainId: string): readonly Method[] {
  return chainId === "sol" ? SOL_METHODS : EVM_METHODS;
}

/* ── Route stages ───────────────────────────────────────────────────────────── */
const STAGES = [
  { id: "app",   label: "Your App", icon: "◈" },
  { id: "edge",  label: "Edge",     icon: "⬡" },
  { id: "pool",  label: "Pool",     icon: "⇄" },
  { id: "node",  label: "Node",     icon: "◉" },
] as const;

type FlowState = "idle" | "firing" | "done";

/* ── Latency arc dial ───────────────────────────────────────────────────────── */
function LatencyArc({ ms, color }: { ms: number; color: string }) {
  const max = 200;
  const pct = Math.min(ms / max, 1);
  const r = 32, cx = 40, cy = 40;
  const circ = 2 * Math.PI * r;
  const arc = 0.75; // 270° sweep
  const dashArray = circ * arc;

  return (
    <svg width="80" height="80" viewBox="0 0 80 80" role="img" aria-label={`Latency ${ms} milliseconds`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="color-mix(in oklab, var(--ink) 7%, transparent)" strokeWidth="4"
        strokeDasharray={`${dashArray} ${circ}`} strokeLinecap="round" transform={`rotate(-225 ${cx} ${cy})`} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={`${dashArray * pct} ${circ}`} strokeLinecap="round" transform={`rotate(-225 ${cx} ${cy})`}
        style={{ transition: "stroke-dasharray 700ms cubic-bezier(0.16,1,0.3,1)" }} />
      <text x={cx} y={cy + 5} textAnchor="middle" fill="var(--ink)" fontSize="15" fontWeight="700">{ms}</text>
      <text x={cx} y={cy + 17} textAnchor="middle" fill="color-mix(in oklab, var(--ink-2) 60%, transparent)" fontSize="7" fontWeight="600">MS</text>
    </svg>
  );
}

/* ── Sparkline that grows in place ──────────────────────────────────────────── */
function MiniSparkline({ data, color, reduced }: { data: number[]; color: string; reduced: boolean }) {
  const w = 140, h = 40;
  if (data.length < 2) return <svg width={w} height={h} aria-hidden />;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / span) * (h - 4) - 2}`).join(" ");
  const last = data[data.length - 1];
  const lastY = h - ((last - min) / span) * (h - 4) - 2;
  const lastX = w;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden className="overflow-visible">
      <defs>
        <linearGradient id={`spark-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#spark-${color.replace("#","")})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color}>
        {/* SVG SMIL animation isn't covered by the global CSS reduced-motion rule — gate explicitly. */}
        {!reduced && (
          <animate attributeName="r" values="2.5;5;2.5" dur="1.6s" repeatCount="indefinite" />
        )}
      </circle>
    </svg>
  );
}

/* ── Route flow with traveling packet ───────────────────────────────────────── */
function RouteFlow({
  flowState,
  color,
  reduced,
  onComplete,
}: {
  flowState: FlowState;
  color: string;
  reduced: boolean;
  onComplete: () => void;
}) {
  const [stageIdx, setStageIdx] = useState(-1);

  useEffect(() => {
    if (flowState !== "firing") {
      setStageIdx(-1);
      return;
    }
    if (reduced) {
      setStageIdx(STAGES.length - 1);
      onComplete();
      return;
    }
    let s = 0;
    setStageIdx(0);
    const stepDur = 180;
    const id = setInterval(() => {
      s++;
      if (s < STAGES.length) {
        setStageIdx(s);
      } else {
        clearInterval(id);
        onComplete();
      }
    }, stepDur);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowState, reduced]);

  return (
    <div className="relative rounded-xl border border-border-subtle bg-bg-elevated px-5 py-6">
      <div className="flex items-center justify-between gap-2">
        {STAGES.map((stage, i) => {
          const lit = stageIdx >= i;
          const isCurrent = stageIdx === i && flowState === "firing";
          const isLast = i === STAGES.length - 1;
          return (
            <div key={stage.id} className="flex flex-1 items-center last:flex-none">
              <div className="flex flex-col items-center gap-2">
                <div
                  className="relative flex h-11 w-11 items-center justify-center rounded-xl border transition-all duration-200"
                  style={{
                    borderColor: lit ? color + "70" : "color-mix(in oklab, var(--ink) 9%, transparent)",
                    background:  lit ? color + "18" : "color-mix(in oklab, var(--ink) 3%, transparent)",
                    boxShadow:   isCurrent ? `0 0 18px ${color}55` : "none",
                  }}
                >
                  <span className="text-base font-bold transition-colors duration-200"
                    style={{ color: lit ? color : "color-mix(in oklab, var(--ink) 30%, transparent)" }}>
                    {stage.icon}
                  </span>
                </div>
                <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.1em] text-text-muted">
                  {stage.label}
                </span>
              </div>
              {!isLast && (
                <div className="relative mx-2 h-[2px] flex-1 self-start overflow-hidden rounded-full" style={{ marginTop: "21px" }}>
                  <div className="absolute inset-0" style={{
                    background: lit ? color + "55" : "color-mix(in oklab, var(--ink) 7%, transparent)",
                    transition: "background 200ms",
                  }} />
                  {lit && !reduced && (
                    <motion.span
                      key={`pkt-${stage.id}-${stageIdx}`}
                      className="absolute top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full"
                      style={{ background: color, boxShadow: `0 0 6px ${color}` }}
                      initial={{ left: "0%" }}
                      animate={{ left: "100%" }}
                      transition={{ duration: 0.18, ease: "linear" }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Method pill ────────────────────────────────────────────────────────────── */
function MethodPill({
  method,
  active,
  onSelect,
}: {
  method: Method;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      role="tab"
      aria-selected={active}
      className="focus-ring group flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-all duration-150"
      style={{
        borderColor: active ? "color-mix(in oklab, var(--accent) 40%, transparent)" : "color-mix(in oklab, var(--ink) 7%, transparent)",
        background:  active ? "color-mix(in oklab, var(--accent) 10%, transparent)" : "color-mix(in oklab, var(--ink) 2%, transparent)",
      }}
    >
      <span className="font-mono text-[12px] font-semibold transition-colors"
        style={{ color: active ? "var(--accent)" : "color-mix(in oklab, var(--ink) 60%, transparent)" }}>
        {method.label}
      </span>
      <ChevronRight size={12} className="shrink-0"
        style={{ color: active ? "var(--accent)" : "color-mix(in oklab, var(--ink) 22%, transparent)" }} aria-hidden />
    </button>
  );
}

/* ── Recent requests list ───────────────────────────────────────────────────── */
type LogEntry = { id: number; method: string; ms: number };

function RecentList({ entries, color }: { entries: LogEntry[]; color: string }) {
  if (entries.length === 0) {
    return (
      <p className="text-center font-mono text-[11px] italic text-text-muted">
        Send a request to populate the log.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5 font-mono text-[11px]">
      <AnimatePresence initial={false}>
        {entries.slice(0, 5).map((e) => (
          <motion.li
            key={e.id}
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0,  height: "auto" }}
            exit={{   opacity: 0, y:  4, height: 0 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="flex items-center justify-between gap-3 overflow-hidden rounded-md border border-border-subtle bg-bg-card/60 px-2.5 py-1.5"
          >
            <span className="truncate text-text-secondary">{e.method}</span>
            <span className="shrink-0 font-semibold tabular-nums" style={{ color }}>
              {e.ms}ms
            </span>
          </motion.li>
        ))}
      </AnimatePresence>
    </ul>
  );
}

/* ── Main section ───────────────────────────────────────────────────────────── */
export default function UnifiedDemo() {
  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const isNear = useNearViewport(sectionRef);

  // ── GSAP: Progressive reveal ──────────────────────────────────────────────
  useGSAP(
    () => {
      if (!sectionRef.current || !isNear) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // Simple fade-in for the whole section — no per-element set/to
        // which breaks with Lenis timing
        gsap.from(sectionRef.current, {
          autoAlpha: 0,
          y: 30,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
          },
        });

        // Phase 3: Send button pulse to invite interaction
        gsap.to(".demo-send-btn", {
          boxShadow: "0 0 24px color-mix(in oklab, var(--accent) 40%, transparent), 0 0 48px color-mix(in oklab, var(--accent) 18%, transparent)",
          repeat: 2,
          yoyo: true,
          duration: 0.8,
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: ".demo-send-btn",
            start: "top 85%",
          },
        });

        // Background glow parallax
        gsap.to(".demo-glow-bg", {
          y: -30,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      });
    },
    { scope: sectionRef, dependencies: [isNear] },
  );

  const [chainIdx, setChainIdx] = useState(0);
  const [methodIdx, setMethodIdx] = useState(0);
  const [flowState, setFlowState] = useState<FlowState>("idle");
  const [observedLatency, setObservedLatency] = useState<number>(CHAINS[0].baseLatency);
  const [latencyHistory, setLatencyHistory] = useState<number[]>([
    CHAINS[0].baseLatency,
    CHAINS[0].baseLatency - 1,
    CHAINS[0].baseLatency,
    CHAINS[0].baseLatency + 1,
    CHAINS[0].baseLatency,
  ]);
  const [recent, setRecent] = useState<LogEntry[]>([]);
  const [block, setBlock] = useState<number>(CHAINS[0].startBlock);
  const logIdRef = useRef(0);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const chain  = CHAINS[chainIdx];
  const methodList = methodsFor(chain.id);
  const safeMethodIdx = Math.min(methodIdx, methodList.length - 1);
  const method = methodList[safeMethodIdx];

  // Block height ticker for the active chain
  useEffect(() => {
    const initId = setTimeout(() => {
      setBlock(chain.startBlock);
      setObservedLatency(chain.baseLatency);
      setLatencyHistory([chain.baseLatency - 1, chain.baseLatency, chain.baseLatency, chain.baseLatency + 1, chain.baseLatency]);
      setRecent([]);
    }, 0);
    const id = setInterval(() => setBlock((b) => b + 1), chain.blockTime * 1000);
    return () => { clearTimeout(initId); clearInterval(id); };
  }, [chain.id, chain.startBlock, chain.baseLatency, chain.blockTime]);

  const fire = () => {
    if (flowState !== "idle") return;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    setFlowState("firing");
  };

  const chainBaseLatency = chain.baseLatency;
  const methodLabel = method.label;
  const handleArrivalRef = useRef<() => void>(null!);
  useEffect(() => {
    handleArrivalRef.current = () => {
      const variance = Math.round((Math.random() - 0.5) * 4);
      const ms = Math.max(3, chainBaseLatency + variance);
      setObservedLatency(ms);
      setLatencyHistory((h) => [...h.slice(-11), ms]);
      setBlock((b) => b + 1);
      logIdRef.current += 1;
      setRecent((r) => [{ id: logIdRef.current, method: methodLabel, ms }, ...r].slice(0, 8));
      setFlowState("done");
      idleTimerRef.current = setTimeout(() => setFlowState("idle"), 2400);
    };
  }, [chainBaseLatency, methodLabel]);
  const handleArrival = () => handleArrivalRef.current?.();

  const requestJson =
    `{
  "jsonrpc": "2.0",
  "method": "${method.id}",
  "params": ${method.params},
  "id": 1
}`;

  return (
    <section ref={sectionRef} className="section-pad relative overflow-hidden bg-surface" aria-label="Try the Instanodes API live">
      <div
        aria-hidden
        className="demo-glow-bg pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 70% 40%, color-mix(in oklab, var(--accent) 6%, transparent) 0%, transparent 60%)",
        }}
      />

      <Container>
        <div>
          <div className="demo-header mb-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <TextReveal
                tag="h2"
                className="font-accent text-[clamp(32px,4.5vw,52px)] font-normal leading-[1.06] tracking-[-0.025em] text-ink"
              >
                Fire a real-shape request.{" "}
                <em className="italic text-accent">Watch it move.</em>
              </TextReveal>
              <p className="mt-4 max-w-xl text-base leading-[1.55] text-ink-2">
                Select a chain and a method, then hit send. Instanodes routes your
                blockchain RPC call through its global node network, and
                the dashboard updates in real time.
              </p>
            </div>
          </div>
        </div>

        {/* Chain pills */}
        <div>
          <div className="demo-chain-pills mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Select chain">
            {CHAINS.map((c, i) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={chainIdx === i}
                onClick={() => { setChainIdx(i); setMethodIdx(0); setFlowState("idle"); }}
                className="ripple-on-click focus-ring relative flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200"
                style={{
                  borderColor: chainIdx === i ? c.color + "60" : "color-mix(in oklab, var(--ink) 9%, transparent)",
                  background:  chainIdx === i ? c.color + "14" : "transparent",
                  color:       chainIdx === i ? "var(--ink)" : "var(--ink-2)",
                }}
              >
                <span className="h-2 w-2 rounded-full"
                  style={{ background: chainIdx === i ? c.color : "color-mix(in oklab, var(--ink) 25%, transparent)" }} />
                {c.name}
                <span className="rounded text-[9px] font-bold px-1.5 py-0.5"
                  style={{
                    background: chainIdx === i ? c.color + "20" : "color-mix(in oklab, var(--ink) 5%, transparent)",
                    color:      chainIdx === i ? c.color : "color-mix(in oklab, var(--ink) 35%, transparent)",
                  }}>
                  {c.tag}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Mobile/tablet: inline compact reflow (everything visible) */}
        <div className="space-y-3 lg:hidden">
          {/* Action row: method dropdown + send */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <select
                aria-label="JSON-RPC method"
                value={method.id}
                onChange={(e) => {
                  const idx = methodList.findIndex((m) => m.id === e.target.value);
                  if (idx >= 0) { setMethodIdx(idx); setFlowState("idle"); }
                }}
                className="focus-ring w-full appearance-none rounded-md border px-3 py-2 pr-8 font-mono text-[11px] font-semibold leading-tight"
                style={{
                  borderColor: "color-mix(in oklab, var(--accent) 40%, transparent)",
                  background: "color-mix(in oklab, var(--accent) 8%, transparent)",
                  color: "var(--accent)",
                }}
              >
                {methodList.map((m) => (
                  <option key={m.id} value={m.id} style={{ background: "var(--surface)", color: "var(--ink)" }}>
                    {m.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                aria-hidden
                className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2"
                style={{ color: "var(--accent)" }}
              />
            </div>
            <button
              onClick={fire}
              disabled={flowState !== "idle"}
              data-cta="demo-send-request"
              data-cta-location="unified-demo-mobile"
              className="demo-send-btn focus-ring inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md px-3.5 py-2 text-[13px] font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 hover:brightness-[0.92]"
              style={{ background: "var(--accent)", border: "1px solid var(--accent)", color: "#ffffff" }}
            >
              {flowState === "idle"   && <>Send <Send size={14} aria-hidden /></>}
              {flowState === "firing" && <>Routing… <Zap size={14} aria-hidden className="animate-pulse" /></>}
              {flowState === "done"   && <>✓ {observedLatency}ms</>}
            </button>
          </div>

          {/* Request (compact) */}
          <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
            <div className="flex items-center justify-between border-b border-border-subtle bg-bg-surface px-3 py-2">
              <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
                <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
                <span className="h-2 w-2 rounded-full bg-[#28C840]" />
                <span className="ml-1.5 font-mono text-[10px] text-text-muted">request.json</span>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-widest text-text-muted">
                POST · /v1/{chain.id}
              </span>
            </div>
            <pre className="max-h-[140px] overflow-auto p-3 font-mono text-[11px] leading-relaxed">
              <code className="text-text-secondary">{requestJson}</code>
            </pre>
          </div>

          {/* Flow (compact horizontal) */}
          <RouteFlow
            flowState={flowState}
            color={chain.color}
            reduced={!!reduced}
            onComplete={handleArrival}
          />

          {/* Response (compact) */}
          <div
            className="overflow-hidden rounded-xl border transition-colors duration-300"
            style={{
              borderColor: flowState === "done" ? "rgba(16,185,129,0.28)" : "color-mix(in oklab, var(--ink) 7%, transparent)",
              background:  flowState === "done" ? "rgba(16,185,129,0.04)" : "rgba(22,22,22,1)",
            }}
          >
            <div className="flex items-center justify-between border-b border-border-subtle bg-bg-surface px-3 py-2">
              <span className="font-mono text-[10px] text-text-muted">response.json</span>
              {flowState === "done" && (
                <span className="flex items-center gap-1.5 text-[10px] font-semibold text-brand-green">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
                  200 OK · {observedLatency}ms
                </span>
              )}
            </div>
            <pre className="max-h-[140px] min-h-[56px] overflow-auto p-3 font-mono text-[11px] leading-relaxed">
              {flowState === "done" ? (
                <code className="block text-text-secondary">
                  {`{\n  "jsonrpc": "2.0",\n  "id": 1,\n  "result": ${method.result}\n}`}
                </code>
              ) : (
                <code className="block italic text-text-muted">
                  {flowState === "firing" ? "Awaiting response…" : "Hit Send to fire the request"}
                </code>
              )}
            </pre>
          </div>

          {/* Live dashboard: 2x2 mini-grid */}
          <div className="overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
            <div className="flex items-center justify-between border-b border-border-subtle bg-bg-surface px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: chain.color }} />
                <span className="text-xs font-semibold text-ink">{chain.name}</span>
              </div>
              <span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-brand-green">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-green opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-green" />
                </span>
                Operational
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 p-2">
              {/* Latency arc */}
              <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-bg-elevated p-2">
                <LatencyArc ms={observedLatency} color={chain.color} />
                <div className="min-w-0">
                  <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted">
                    Avg lat.
                  </p>
                  <p className="mt-0.5 text-[10px] text-text-muted">
                    p99{" "}
                    <span className="font-semibold text-text-secondary">{chain.baseP99}ms</span>
                  </p>
                </div>
              </div>

              {/* Block height */}
              <div className="rounded-lg border border-border-subtle bg-bg-elevated p-2.5">
                <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted">
                  Block height
                </p>
                <p className="mt-1 break-all font-mono text-sm font-bold tabular-nums text-ink">
                  {block.toLocaleString('en-US')}
                </p>
                <div className="mt-1 flex items-center gap-1.5 text-[9px] text-text-muted">
                  <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: chain.color }} />
                  ~{chain.blockTime < 1 ? `${chain.blockTime * 1000}ms` : `${chain.blockTime}s`}
                </div>
              </div>

              {/* Sparkline (span both columns) */}
              <div className="col-span-2 rounded-lg border border-border-subtle bg-bg-elevated p-2.5">
                <p className="mb-1 text-[9px] font-bold uppercase tracking-widest text-text-muted">
                  Observed latency · last {latencyHistory.length}
                </p>
                <MiniSparkline data={latencyHistory} color={chain.color} reduced={!!reduced} />
              </div>
            </div>
          </div>

          {/* Recent requests (last 3, compact) */}
          <div className="rounded-xl border border-border-subtle bg-bg-card p-3">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-text-muted">
              Recent requests
            </p>
            <RecentList entries={recent.slice(0, 3)} color={chain.color} />
          </div>
        </div>

        {/* Desktop: 3-column grid */}
        <div className="hidden lg:block">
          <div className="demo-grid grid gap-4 lg:grid-cols-[220px_1fr_320px]">

            {/* ── Methods column ─────────────────────────────────────── */}
            <div className="demo-col rounded-2xl border border-border-subtle bg-bg-card p-4">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-text-muted">
                JSON-RPC method
              </p>
              <div className="space-y-1" role="tablist" aria-label="Choose a JSON-RPC method">
                {methodList.map((m, i) => (
                  <MethodPill key={m.id} method={m} active={safeMethodIdx === i}
                    onSelect={() => { setMethodIdx(i); setFlowState("idle"); }} />
                ))}
              </div>

              <button
                onClick={fire}
                disabled={flowState !== "idle"}
                data-cta="demo-send-request"
                data-cta-location="unified-demo"
                className="demo-send-btn focus-ring mt-4 flex min-h-[42px] w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 hover:brightness-[0.92]"
                style={{
                  background: "var(--accent)",
                  border: "1px solid var(--accent)",
                  color: "#ffffff",
                }}
              >
                {flowState === "idle"   && <>Send request <Send size={14} aria-hidden /></>}
                {flowState === "firing" && <>Routing… <Zap size={14} aria-hidden className="animate-pulse" /></>}
                {flowState === "done"   && <>Sent · {observedLatency}ms ✓</>}
              </button>
            </div>

            {/* ── Center: Request → Flow → Response ───────────────────── */}
            <div className="demo-col flex flex-col gap-4">
              {/* Request panel */}
              <div className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-card">
                <div className="flex items-center justify-between border-b border-border-subtle bg-bg-surface px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
                    <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
                    <span className="ml-2 font-mono text-[11px] text-text-muted">request.json</span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                    POST · /v1/{chain.id}
                  </span>
                </div>
                <pre className="overflow-x-auto p-4 font-mono text-[12px] leading-relaxed">
                  <code className="text-text-secondary">{requestJson}</code>
                </pre>
              </div>

              {/* Flow */}
              <RouteFlow
                flowState={flowState}
                color={chain.color}
                reduced={!!reduced}
                onComplete={handleArrival}
              />

              {/* Response panel */}
              <div className="overflow-hidden rounded-2xl border transition-colors duration-300"
                style={{
                  borderColor: flowState === "done" ? "rgba(16,185,129,0.28)" : "color-mix(in oklab, var(--ink) 7%, transparent)",
                  background:  flowState === "done" ? "rgba(16,185,129,0.04)" : "rgba(22,22,22,1)",
                }}
              >
                <div className="flex items-center justify-between border-b border-border-subtle bg-bg-surface px-4 py-2.5">
                  <span className="font-mono text-[11px] text-text-muted">response.json</span>
                  <AnimatePresence mode="popLayout">
                    {flowState === "done" && (
                      <motion.span
                        key="ok"
                        initial={{ opacity: 0, x: 6 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{   opacity: 0, x: -6 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-green"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
                        200 OK · {observedLatency}ms
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>
                <pre className="min-h-[64px] overflow-x-auto p-4 font-mono text-[12px] leading-relaxed">
                  <AnimatePresence mode="wait">
                    {flowState === "done" ? (
                      <motion.code
                        key={`r-${methodIdx}-${recent[0]?.id ?? 0}`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                        className="block text-text-secondary"
                      >
                        {`{\n  "jsonrpc": "2.0",\n  "id": 1,\n  "result": ${method.result}\n}`}
                      </motion.code>
                    ) : (
                      <motion.code
                        key="placeholder"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="block italic text-text-muted"
                      >
                        {flowState === "firing" ? "Awaiting response…" : "Hit Send to fire the request"}
                      </motion.code>
                    )}
                  </AnimatePresence>
                </pre>
              </div>
            </div>

            {/* ── Right: Live dashboard ──────────────────────────────── */}
            <div className="demo-col flex flex-col gap-4">
              <div className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-card">
                <div className="flex items-center justify-between border-b border-border-subtle bg-bg-surface px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: chain.color }} />
                    <span className="font-semibold text-sm text-ink">{chain.name}</span>
                  </div>
                  <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-brand-green">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-green opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand-green" />
                    </span>
                    Operational
                  </span>
                </div>

                <div className="space-y-4 p-4">
                  {/* Latency arc */}
                  <div className="flex items-center gap-4">
                    <LatencyArc ms={observedLatency} color={chain.color} />
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
                        Avg latency
                      </p>
                      <p className="mt-0.5 text-[11px] text-text-muted">
                        p99 <span className="font-semibold text-text-secondary">{chain.baseP99}ms</span>
                      </p>
                    </div>
                  </div>

                  {/* Block height */}
                  <div className="rounded-lg border border-border-subtle bg-bg-elevated p-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted">
                      Block height
                    </p>
                    <p className="mt-1 font-mono text-lg font-bold tabular-nums text-ink">
                      {block.toLocaleString('en-US')}
                    </p>
                    <div className="mt-1 flex items-center gap-1.5 text-[10px] text-text-muted">
                      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: chain.color }} />
                      ~{chain.blockTime < 1 ? `${chain.blockTime * 1000}ms` : `${chain.blockTime}s`} block time
                    </div>
                  </div>

                  {/* Latency sparkline */}
                  <div className="rounded-lg border border-border-subtle bg-bg-elevated p-3">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-text-muted">
                      Observed latency · last {latencyHistory.length}
                    </p>
                    <MiniSparkline data={latencyHistory} color={chain.color} reduced={!!reduced} />
                  </div>
                </div>
              </div>

              {/* Recent requests */}
              <div className="rounded-2xl border border-border-subtle bg-bg-card p-4">
                <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-text-muted">
                  Recent requests
                </p>
                <RecentList entries={recent} color={chain.color} />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
